import { Stack } from '@mui/system';
import { useCallback, useEffect } from 'react';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { TwilioError } from 'twilio-video';
import { GetVisitByIdVisitTypeEnum } from 'woofwoof-api';
import LiveVisitFooter from '../../conversation/components/LiveVisitFooter';
import LiveVisitHeader from '../../conversation/components/LiveVisitHeader';
import LiveVisitVideo from '../../conversation/components/LiveVisitVideo';
import { ParticipantProvider } from '../../conversation/context/ParticipantProvider';
import { VideoProvider } from '../../conversation/context/VideoProvider';
import { useMicrophoneAndCameraPermissionsModalContext } from '../../conversation/hooks/useMicrophoneAndCameraPermissionsModalContext';
import { useLiveVisitContext } from '../hooks/useLiveVisitContext';
import VisitChatRoom from './VisitChatRoom';

export default function LiveVisitContent() {
  const { data, isError } = useLiveVisitContext();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const navigate = useNavigate();
  const { openModal } = useMicrophoneAndCameraPermissionsModalContext();

  const isChat = data?.visitType === GetVisitByIdVisitTypeEnum.Chat;
  const conversation = data?.conversation as { id: string } | undefined;

  const handleOnError = useCallback(
    (err: TwilioError | Error) => {
      console.error('Video error:', err);

      // Check if it's a permissions error
      const isPermissionError =
        err.message?.includes('Permission denied') ||
        err.message?.includes('NotAllowedError') ||
        (err as TwilioError).code === 20151; // Twilio permission denied code

      if (isPermissionError) {
        // Open permissions modal and let user try again
        openModal();
        return; // Don't navigate away, give user a chance to grant permissions
      }

      // Room not found errors are now handled centrally in useRoom hook
      // No need for component-level handling

      // For other errors, show error message and navigate away
      enqueueTranslated('snackbar.videoConnectionError', {
        variant: 'error',
      });
      navigate(PATH_DASHBOARD.root);
    },
    [navigate, openModal, enqueueTranslated],
  );

  useEffect(() => {
    if (isError) {
      enqueueTranslated('snackbar.conversationDetailsError', {
        variant: 'error',
      });
      navigate(PATH_DASHBOARD.general.upcomingVisits);
    }
  }, [isError, enqueueTranslated, navigate]);

  if (isChat && conversation?.id) {
    return <VisitChatRoom conversationId={conversation.id} variant="full" />;
  }

  // Video visits do not expose chat.
  return (
    <VideoProvider onError={handleOnError}>
      <ParticipantProvider>
        <Stack style={{ flex: 1, height: '100%' }}>
          <LiveVisitHeader />
          <LiveVisitVideo />
          <LiveVisitFooter />
        </Stack>
      </ParticipantProvider>
    </VideoProvider>
  );
}
